import { create } from "zustand";

import { api, type AuthUser } from "@/lib/api";
import {
  clearSessionTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setRefreshToken,
} from "@/lib/auth";

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  forceLogout: () => void;
  /** A sessão acabou em OUTRA aba (T-35) — ver `SessionAcrossTabs`. */
  sessionEndedElsewhere: () => void;
  bootstrapSession: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

let bootstrapSessionPromise: Promise<void> | null = null;

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  accessToken: getAccessToken(),
  isAuthenticated: false,
  loading: true,

  async login(email, password) {
    const session = await api.login(email, password);
    setAccessToken(session.access_token);
    setRefreshToken(session.refresh_token);
    set({
      user: session.user,
      accessToken: session.access_token,
      isAuthenticated: true,
      loading: false,
    });
  },

  async changePassword(currentPassword, newPassword) {
    // A resposta traz o usuário já sem a obrigação de trocar a senha, então o
    // guard libera a navegação sem uma segunda ida ao servidor.
    //
    // **E traz tokens novos, que precisam ser gravados aqui.** Desde a F-S2 a
    // troca de senha invalida as credenciais antigas no servidor: guardar só o
    // `user`, como esta função fazia, deslogaria a pessoa na requisição
    // seguinte — e no primeiro acesso obrigatório isso é um laço fechado, já
    // que a tela de troca é a única que ela pode abrir.
    const session = await api.changePassword(currentPassword, newPassword);
    setAccessToken(session.access_token);
    setRefreshToken(session.refresh_token);
    set({
      user: session.user,
      accessToken: session.access_token,
      isAuthenticated: true,
    });
  },

  async logout() {
    try {
      await api.logout();
    } catch {
      // logout local deve funcionar mesmo com erro no servidor.
    }
    clearSessionTokens();
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      loading: false,
    });
  },

  forceLogout() {
    clearSessionTokens();
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      loading: false,
    });
    if (typeof window !== "undefined") {
      window.location.href = "/login";
    }
  },

  sessionEndedElsewhere() {
    // Idempotente: a outra aba já apagou os dois tokens do storage; aqui é a
    // memória desta aba e o estado da tela. Sem `window.location`: a rota
    // protegida navega sozinha para /login quando `isAuthenticated` cai, e a
    // pública (/v/:slug) continua aberta, agora como anônima.
    clearSessionTokens();
    set({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      loading: false,
    });
  },

  async bootstrapSession() {
    if (bootstrapSessionPromise) {
      return bootstrapSessionPromise;
    }

    bootstrapSessionPromise = (async () => {
      set({ loading: true });

      const access = getAccessToken();
      if (access) {
        try {
          const me = await api.me();
          set({
            user: me.user,
            accessToken: getAccessToken(),
            isAuthenticated: true,
            loading: false,
          });
          return;
        } catch {
          setAccessToken(null);
        }
      }

      const refresh = getRefreshToken();
      if (!refresh) {
        clearSessionTokens();
        set({ user: null, accessToken: null, isAuthenticated: false, loading: false });
        return;
      }

      try {
        const session = await api.refresh(refresh);
        setAccessToken(session.access_token);
        setRefreshToken(session.refresh_token);
        set({
          user: session.user,
          accessToken: session.access_token,
          isAuthenticated: true,
          loading: false,
        });
      } catch {
        clearSessionTokens();
        set({ user: null, accessToken: null, isAuthenticated: false, loading: false });
      }
    })().finally(() => {
      bootstrapSessionPromise = null;
    });

    return bootstrapSessionPromise;
  },
}));
